import React, { useCallback } from 'react';
import { CommonActions } from '@react-navigation/native';
import { getSyncingStatus, listProviderAccounts, resetRenewingConnection, setConnectionsStatus, } from 'actions';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import LogoBankAccount from 'design-system/LogoBankAccount';
import { LIST_PROVIDER_ACCOUNTS_SUCCESS } from 'actions/types';
import { ConnectionUpdateFlow, ConnectionUpdateStep, } from 'utils/types/navigation';
import { LoadingAppViewModal } from 'design-system/LoadingAppView';
import useAppRating from 'hooks/useAppRating';
import useRefreshAccounts from 'hooks/useRefreshAccounts';
import { selectUserTier } from 'features/premium/selectors/tier';
import { isUnlocked } from 'utils';
import { benefits } from 'features/premium/benefits';
import { selectIsDefaultSpace } from 'features/spaces/reducers/selectors';
import { getMySpaces } from 'features/spaces/actions';
import { isIOS } from '../../../constants';
export const useRenewedConnection = () => {
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const refreshAccounts = useRefreshAccounts();
    const rateApp = useAppRating();
    const navigateToSuccess = useCallback((bankInfo) => {
        setTimeout(() => dispatch(resetRenewingConnection()), 300);
        dispatch(setConnectionsStatus(true));
        dispatch(getSyncingStatus());
        dispatch(getMySpaces());
        navigation.navigate('Success', {
            title: 'Success',
            icon: <LogoBankAccount data={bankInfo} size="R56"/>,
            subTitle: `Your connection with ${bankInfo.name} has been renewed!`,
            onContinue: () => {
                navigation.goBack();
                rateApp();
                return true;
            },
            buttonText: 'Done',
        });
    }, [rateApp]);
    const handleRenewedConnection = useCallback(async (renewingConnection) => {
        LoadingAppViewModal.show({ title: 'Importing your accounts' });
        navigation.dispatch((state) => {
            const routes = state.routes.filter((route) => route.name !== 'ConnectionUpdate');
            if (state.type !== 'stack' || routes.length === state.routes.length) {
                return CommonActions.reset(state);
            }
            return CommonActions.reset({
                ...state,
                routes,
                index: routes.length - 1,
            });
        });
        const res = await dispatch(listProviderAccounts(renewingConnection));
        if (res?.type === LIST_PROVIDER_ACCOUNTS_SUCCESS) {
            // Imported accounts change the balance history graphs
            refreshAccounts(res.payload.accounts.map((account) => account.type));
        }
        const state = store.getState();
        const { importedAccounts } = state.linkBank;
        if (res?.type !== LIST_PROVIDER_ACCOUNTS_SUCCESS ||
            (!importedAccounts?.missing.length && !importedAccounts?.new.length)) {
            navigateToSuccess(renewingConnection.bankInfo);
        }
        else {
            const steps = [];
            if (importedAccounts.new.length) {
                steps.push(ConnectionUpdateStep.NEW);
                const addedBusinessAccount = importedAccounts.new.some((account) => account.isBusiness);
                if (addedBusinessAccount &&
                    !isUnlocked(selectUserTier(state), benefits.business.minTier)) {
                    steps.push(ConnectionUpdateStep.BUSINESS);
                }
            }
            if (importedAccounts.missing.length) {
                steps.push(ConnectionUpdateStep.MISSING);
            }
            if (!selectIsDefaultSpace(state) && importedAccounts.new.length) {
                steps.push(ConnectionUpdateStep.SPACE);
            }
            navigation.navigate('ConnectionUpdate', {
                flow: ConnectionUpdateFlow.RENEW,
                steps,
                stepIndex: 0,
            });
        }
        setTimeout(() => LoadingAppViewModal.hide(), 
        // Waiting for navigation animation
        isIOS ? 300 : 100);
    }, [navigateToSuccess, refreshAccounts]);
    return { handleRenewedConnection, navigateToSuccess };
};
export default useRenewedConnection;
